import React, { RefObject, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable } from "@fortawesome/free-solid-svg-icons";

interface TablePickerProps {
    isOpen: boolean;
    hoveredSize: { rows: number; columns: number };
    pickerRef: RefObject<HTMLDivElement>;
    popoverRef: RefObject<HTMLDivElement>;
    onToggle: () => void;
    onHoverSize: (size: { rows: number; columns: number }) => void;
    onInsertTable: (rows: number, columns: number) => void;
}

export const TablePicker: React.FC<TablePickerProps> = ({
    isOpen,
    hoveredSize,
    pickerRef,
    popoverRef,
    onToggle,
    onHoverSize,
    onInsertTable,
}) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [position, setPosition] = useState<React.CSSProperties>({ visibility: 'hidden' });

    useLayoutEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            const trigger = triggerRef.current;
            const popover = popoverRef.current;
            if (!trigger || !popover) return;

            const triggerRect = trigger.getBoundingClientRect();
            const popoverRect = popover.getBoundingClientRect();
            const viewport = window.visualViewport;
            const viewportLeft = viewport?.offsetLeft || 0;
            const viewportTop = viewport?.offsetTop || 0;
            const viewportWidth = viewport?.width || window.innerWidth;
            const left = Math.max(
                viewportLeft + 8,
                Math.min(triggerRect.left, viewportLeft + viewportWidth - popoverRect.width - 8),
            );
            const top = Math.max(viewportTop + 8, triggerRect.top - popoverRect.height - 8);

            setPosition({ left: `${left}px`, top: `${top}px`, visibility: 'visible' });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        window.visualViewport?.addEventListener('resize', updatePosition);
        window.visualViewport?.addEventListener('scroll', updatePosition);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            window.visualViewport?.removeEventListener('resize', updatePosition);
            window.visualViewport?.removeEventListener('scroll', updatePosition);
        };
    }, [isOpen, popoverRef]);

    return <div className="note-formatting-table-picker-wrapper" ref={pickerRef}>
        <button
            ref={triggerRef}
            type="button"
            className={`note-formatting-toolbar-button ${isOpen ? "active" : ""}`}
            aria-label="Insert table"
            aria-expanded={isOpen}
            title="Insert table"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onToggle}
        >
            <FontAwesomeIcon icon={faTable} />
        </button>
        {isOpen && createPortal(
            <div ref={popoverRef} className="note-formatting-table-picker" role="menu" aria-label="Table size" style={position}>
                <div className="note-formatting-table-picker-label">
                    {hoveredSize.columns} x {hoveredSize.rows}
                </div>
                <div className="note-formatting-table-picker-grid">
                    {Array.from({ length: 4 }, (_, rowIndex) =>
                        Array.from({ length: 4 }, (_, columnIndex) => {
                            const rows = rowIndex + 1;
                            const columns = columnIndex + 1;
                            const selected = rows <= hoveredSize.rows && columns <= hoveredSize.columns;

                            return (
                                <button
                                    key={`${rows}-${columns}`}
                                    type="button"
                                    className={`note-formatting-table-picker-cell ${selected ? "selected" : ""}`}
                                    aria-label={`Insert ${columns} by ${rows} table`}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onMouseEnter={() => onHoverSize({ rows, columns })}
                                    onFocus={() => onHoverSize({ rows, columns })}
                                    onClick={() => onInsertTable(rows, columns)}
                                />
                            );
                        })
                    )}
                </div>
            </div>,
            document.body,
        )}
    </div>
};
